import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import s, { colors } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'
const LIMIT_OPTIONS = [50, 100, 250, 500]

const selFilter = { ...s.select, width: 'auto', maxWidth: '220px', minWidth: 0 }

function findNode(nodes, code) {
  for (const n of nodes) {
    if (n.category_code === code) return n
    if (n.children && n.children.length > 0) {
      const found = findNode(n.children, code)
      if (found) return found
    }
  }
  return null
}

function Products() {
  const { selectedBrand } = useBrand()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [filter, setFilter] = useState('all')
  const [categoryCode, setCategoryCode] = useState('')
  const [categoryTree, setCategoryTree] = useState([])
  const [categoryChain, setCategoryChain] = useState([])
  const [filterGroups, setFilterGroups] = useState([])
  const [selectedFilterGroup, setSelectedFilterGroup] = useState('')
  const [filterOptions, setFilterOptions] = useState([])
  const [selectedFilterCode, setSelectedFilterCode] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(50)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    if (!selectedBrand) return
    fetchCategories()
    fetchFilterGroups()
  }, [selectedBrand]) // eslint-disable-line

  useEffect(() => {
    if (!selectedBrand) return
    fetchProducts()
  }, [selectedBrand, page, limit, search, filter, categoryCode, selectedFilterCode]) // eslint-disable-line

  useEffect(() => {
    if (!selectedBrand || !selectedFilterGroup) {
      setFilterOptions([])
      return
    }
    fetchFilterOptions(selectedFilterGroup)
  }, [selectedFilterGroup]) // eslint-disable-line

  function fetchCategories() {
    const token = localStorage.getItem('adminToken')
    axios.get(`${API}/admin/brands/${selectedBrand.id}/categories`, {
      headers: { Authorization: token }
    }).then(res => {
      setCategoryTree(res.data.tree || [])
    }).catch(() => {})
  }

  function fetchFilterGroups() {
    const token = localStorage.getItem('adminToken')
    axios.get(`${API}/admin/brands/${selectedBrand.id}/filter-groups`, {
      headers: { Authorization: token }
    }).then(res => {
      setFilterGroups(res.data || [])
    }).catch(() => {})
  }

  function fetchFilterOptions(groupCode) {
    const token = localStorage.getItem('adminToken')
    axios.get(`${API}/admin/brands/${selectedBrand.id}/filter-options/${groupCode}`, {
      headers: { Authorization: token }
    }).then(res => {
      setFilterOptions(res.data || [])
    }).catch(() => {})
  }

  function fetchProducts() {
    if (!selectedBrand) return
    setLoading(true)
    const token = localStorage.getItem('adminToken')
    const params = new URLSearchParams({
      page,
      limit,
      ...(search && { search }),
      ...(filter !== 'all' && { stock: filter }),
      ...(categoryCode && { categoryCode }),
      ...(selectedFilterCode && { filterCode: selectedFilterCode })
    })
    axios.get(`${API}/admin/brands/${selectedBrand.id}/products?${params}`, {
      headers: { Authorization: token }
    }).then(res => {
      setProducts(res.data.products)
      setTotal(res.data.total)
      setTotalPages(res.data.totalPages)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  function handleSearch(e) {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  function handleFilterChange(val) {
    setFilter(val)
    setPage(1)
  }

  function handleLimitChange(val) {
    setLimit(parseInt(val))
    setPage(1)
  }

  function handleCategorySelect(depth, code) {
    if (!code) {
      const newChain = categoryChain.slice(0, depth)
      setCategoryChain(newChain)
      setCategoryCode(newChain.length > 0 ? newChain[newChain.length - 1] : '')
      setPage(1)
      return
    }
    const newChain = [...categoryChain.slice(0, depth), code]
    setCategoryChain(newChain)
    setCategoryCode(code)
    setPage(1)
  }

  function handleClearCategory() {
    setCategoryChain([])
    setCategoryCode('')
    setPage(1)
  }

  function handleFilterGroupChange(groupCode) {
    setSelectedFilterGroup(groupCode)
    setSelectedFilterCode('')
    setPage(1)
  }

  function handleFilterOptionChange(optionCode) {
    setSelectedFilterCode(optionCode)
    setPage(1)
  }

  function handleClearFilters() {
    setSelectedFilterGroup('')
    setSelectedFilterCode('')
    setFilterOptions([])
    setPage(1)
  }

  // Kategori dropdown zinciri
  const dropdowns = []
  dropdowns.push(
    <select
      key="root"
      value={categoryChain[0] || ''}
      onChange={e => handleCategorySelect(0, e.target.value)}
      style={selFilter}
    >
      <option value="">Ana Kategori Seç</option>
      {categoryTree.filter(c => c.category_name).map(c => (
        <option key={c.category_code} value={c.category_code}>{c.category_name}</option>
      ))}
    </select>
  )

  for (let depth = 0; depth < categoryChain.length; depth++) {
    const node = findNode(categoryTree, categoryChain[depth])
    if (!node || !node.children || node.children.filter(c => c.category_name).length === 0) break
    const d = depth
    dropdowns.push(
      <select
        key={`depth-${depth}`}
        value={categoryChain[depth + 1] || ''}
        onChange={e => handleCategorySelect(d + 1, e.target.value)}
        style={selFilter}
      >
        <option value="">Tüm Alt Kategoriler</option>
        {node.children.filter(c => c.category_name).map(c => (
          <option key={c.category_code} value={c.category_code}>{c.category_name}</option>
        ))}
      </select>
    )
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px 16px' }}>
      <p style={{ color: colors.textSecondary, marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  return (
    <div>
      <div className="sx-wrap" style={{ ...s.flexBetween, marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 style={s.pageTitle}>Ürünler <span style={{ fontSize: '15px', color: colors.textSecondary, fontWeight: 400 }}>({total} ürün)</span></h2>
      </div>

      <div className="sx-wrap" style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', maxWidth: '100%' }}>
          <input
            placeholder="Ürün ara..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ ...s.input, width: '220px', maxWidth: '100%', minWidth: 0 }}
          />
          <button type="submit" style={s.btnPrimary}>Ara</button>
        </form>

        {dropdowns}

        {categoryCode && (
          <button onClick={handleClearCategory} style={{
            ...s.btn, background: colors.dangerLight, color: colors.dangerText
          }}>✕ Kategori Temizle</button>
        )}

        <select value={filter} onChange={e => handleFilterChange(e.target.value)} style={selFilter}>
          <option value="all">Tümü</option>
          <option value="instock">Stokta</option>
          <option value="outofstock">Stok Dışı</option>
        </select>

        <select value={limit} onChange={e => handleLimitChange(e.target.value)} style={selFilter}>
          {LIMIT_OPTIONS.map(l => <option key={l} value={l}>{l} ürün</option>)}
        </select>
      </div>

      {/* Filtre grupları */}
      {filterGroups.length > 0 && (
        <div className="sx-wrap" style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={selectedFilterGroup}
            onChange={e => handleFilterGroupChange(e.target.value)}
            style={selFilter}
          >
            <option value="">Filtre Grubu Seç</option>
            {filterGroups.map(g => (
              <option key={g.group_code} value={g.group_code}>{g.group_name}</option>
            ))}
          </select>

          {filterOptions.length > 0 && (
            <select
              value={selectedFilterCode}
              onChange={e => handleFilterOptionChange(e.target.value)}
              style={selFilter}
            >
              <option value="">Tüm Değerler</option>
              {filterOptions.map(o => (
                <option key={o.option_code} value={o.option_code}>{o.filter_value}</option>
              ))}
            </select>
          )}

          {(selectedFilterGroup || selectedFilterCode) && (
            <button onClick={handleClearFilters} style={{
              ...s.btn, background: colors.dangerLight, color: colors.dangerText
            }}>✕ Filtre Temizle</button>
          )}
        </div>
      )}

      {categoryCode && (
        <div className="sx-break" style={{ ...s.alertInfo, marginBottom: '8px' }}>
          Kategori kodu: <strong>{categoryCode}</strong> — Segment kurallarında bu kodu kullanabilirsiniz
        </div>
      )}

      {selectedFilterCode && (
        <div className="sx-break" style={{ ...s.alertInfo, marginBottom: '12px' }}>
          Filtre kodu: <strong>{selectedFilterCode}</strong> — Segment kurallarında bu kodu kullanabilirsiniz
        </div>
      )}

      {loading ? <p style={{ color: colors.textSecondary }}>Yükleniyor...</p> : (
        <>
          <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
            <div className="sx-scroll-x">
            <table className="sx-cards" style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Görsel</th>
                  <th style={s.th}>Ürün Adı</th>
                  <th style={s.th}>Marka</th>
                  <th style={s.th}>Kategori</th>
                  <th style={s.th}>Kat. Kodu</th>
                  <th style={s.th}>Fiyat</th>
                  <th style={s.th}>Stok</th>
                  <th style={s.th}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td data-label="Görsel" style={s.td}>
                      {p.image_url
                        ? <img src={p.image_url} alt={p.name} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px' }} />
                        : <div style={{ width: '48px', height: '48px', background: colors.bgHover, borderRadius: '4px' }} />}
                    </td>
                    <td data-label="Ürün Adı" style={s.td}>{p.name}</td>
                    <td data-label="Marka" style={{ ...s.td, color: colors.textSecondary }}>{p.brand}</td>
                    <td data-label="Kategori" style={{ ...s.td, color: colors.textSecondary }}>{p.category}</td>
                    <td data-label="Kat. Kodu" style={{ ...s.td, fontSize: '12px', color: colors.info, fontFamily: 'monospace' }}>{p.categories}</td>
                    <td data-label="Fiyat" style={{ ...s.td, fontWeight: 600 }}>{parseFloat(p.price).toLocaleString('tr-TR')} ₺</td>
                    <td data-label="Stok" style={s.td}>{p.stock}</td>
                    <td data-label="Durum" style={s.td}>
                      <span style={p.stock_status === 'instock' ? s.badgeSuccess : s.badgeDanger}>
                        {p.stock_status === 'instock' ? 'Stokta' : 'Stok Dışı'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          <div className="sx-wrap" style={{ ...s.flexBetween, flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: colors.textSecondary }}>
              {(page - 1) * limit + 1} - {Math.min(page * limit, total)} / {total} ürün
            </span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  ...s.btnPrimary,
                  ...(page === 1 ? { background: colors.bgHover, color: colors.textMuted, cursor: 'not-allowed' } : {})
                }}
              >← Önceki</button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(page - 2 + i, totalPages - 4 + i))
                return (
                  <button key={pageNum} onClick={() => setPage(pageNum)}
                    style={page === pageNum ? s.btnPrimary : s.btnSecondary}>{pageNum}</button>
                )
              })}

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  ...s.btnPrimary,
                  ...(page === totalPages ? { background: colors.bgHover, color: colors.textMuted, cursor: 'not-allowed' } : {})
                }}
              >Sonraki →</button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Products
